/**
 * API Client for the backtesting platform.
 * Provides typed functions for all API endpoints.
 */

import { boundSavePayload } from '../../core/save-payload-helpers.js';
import type {
  ApiBacktestResult,
  BacktestSummary,
  PaginatedHistory,
  Candle,
  CandleRequest,
  AggregateBacktestResult,
  StrategyDetails,
  StrategyInfo,
  ApiError,
  OptimizationResult,
  ScanRequest,
  ScanResultRow,
  ScanSummary,
  AggregationConfig,
  CreateAggregationRequest,
  UpdateAggregationRequest,
  PaperSession,
  PaperSessionDetail,
  PaperTradesResponse,
  PaperEquitySnapshot,
  PaperTradingEvent,
  CreatePaperSessionRequest,
  PaperSessionEventsResponse,
  StrategyConfigEntity,
  StrategyConfigListItem,
  KillSwitchSettings,
  KillSwitchConfig,
  UpdateKillSwitchRequest,
  CredentialSummary,
  CreateCredentialRequest,
  TestCredentialResult,
  UserStrategy,
  StrategyParam,
  ApiTokenSummary,
  ApiTokenCreated,
  CreateApiTokenRequest,
  AuthUser,
  UpdateOnboardingRequest,
  MigrateStrategyConfigResponse,
  MigrateAggregationResponse,
} from '../types';

const API_BASE = '/api';

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = { ...options.headers };
  if (options.body) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  // Inject auth token
  const authToken = localStorage.getItem('auth_token');
  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle 401 - clear auth and reload (only if user was previously authenticated)
    if (response.status === 401) {
      const wasAuthenticated = !!localStorage.getItem('auth_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      if (wasAuthenticated) {
        window.location.reload();
      }
      throw new ApiClientError('Authentication required', 401);
    }

    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    let details: unknown;

    try {
      const errorData: ApiError = await response.json();
      errorMessage = errorData.message || errorMessage;
      details = errorData;
    } catch {
      // Response was not JSON, use default message
    }

    throw new ApiClientError(errorMessage, response.status, details);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Like apiFetch but returns the raw Response object.
 * Use this for SSE streaming endpoints and blob downloads where the caller
 * needs to process the response body directly.
 *
 * Unlike apiFetchStream, this does NOT throw on non-ok responses (except 401).
 * Callers are responsible for checking response.ok themselves.
 */
export async function apiFetchRaw(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers: Record<string, string> = {};

  // Copy existing headers
  if (options.headers) {
    const existing = options.headers;
    if (existing instanceof Headers) {
      existing.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(existing)) {
      for (const [key, value] of existing) {
        headers[key] = value;
      }
    } else {
      Object.assign(headers, existing as Record<string, string>);
    }
  }

  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Inject auth token
  const authToken = localStorage.getItem('auth_token');
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 — clear auth and reload (only if user was previously authenticated)
  if (response.status === 401) {
    const wasAuthenticated = !!localStorage.getItem('auth_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    if (wasAuthenticated) {
      window.location.reload();
    }
    throw new ApiClientError('Authentication required', 401);
  }

  return response;
}

/**
 * Like apiFetchRaw but also throws ApiClientError on any non-ok response
 * (not just 401). Use this for SSE streaming endpoints where any HTTP error
 * should be surfaced as an exception.
 */
export async function apiFetchStream(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
  const response = await apiFetchRaw(url, options);

  if (!response.ok) {
    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    let details: unknown;
    try {
      const errorData = await response.json() as { message?: string };
      errorMessage = errorData.message ?? errorMessage;
      details = errorData;
    } catch {
      // Response body was not JSON
    }
    throw new ApiClientError(errorMessage, response.status, details);
  }

  return response;
}

// ============================================================================
// Backtest Endpoints
// ============================================================================

/**
 * Submit a client-computed backtest result to the server for persistence.
 *
 * @param idempotencyKey  A UUID v4 generated once per logical backtest run
 *   (NOT per retry). Pass the same key on retries so the server returns the
 *   previously-stored result instead of inserting a duplicate.
 *   Generate with: `crypto.randomUUID()` at the time the run is queued.
 */
export async function submitClientBacktest(
  body: {
    id: string;
    config: {
      strategyName: string;
      symbol: string;
      timeframe: string;
      startDate: number;
      endDate: number;
      initialCapital: number;
      exchange?: string;
      mode?: 'spot' | 'futures';
      params?: Record<string, unknown>;
    };
    trades: unknown[];
    equity: Array<{ timestamp: number; equity: number; drawdown?: number }>;
    metrics: Record<string, unknown>;
    /** Strategy log entries to persist (from ctx.log() calls). */
    strategyLogs?: Array<{ timestamp: number; message: string }>;
    /** Per-asset breakdown for aggregation runs */
    perAssetResults?: Record<string, unknown>;
    /** Signal history for aggregation runs */
    signalHistory?: unknown[];
    /** Aggregation ID for multi-asset runs */
    aggregationId?: string;
    /**
     * CP-1: strategy config id for single-strategy agg-of-one runs (dashboard).
     * Tells the server to link this MULTI result to the aof1- aggregation record.
     */
    strategyConfigId?: string;
  },
  idempotencyKey?: string,
): Promise<{ id: string; replay?: boolean }> {
  const extraHeaders: Record<string, string> = {};
  if (idempotencyKey !== undefined) {
    extraHeaders['Idempotency-Key'] = idempotencyKey;
  }

  // BUG-22: Downsample equity series to avoid HTTP 413 Payload Too Large.
  // The boundSavePayload helper caps equity to DEFAULT_EQUITY_CAP (3000) points,
  // always preserving first + last. Trades and metrics are never modified.
  const bounded = boundSavePayload({
    id: body.id,
    config: body.config as Record<string, unknown>,
    trades: body.trades,
    equity: body.equity,
    metrics: body.metrics,
    perAssetResults: body.perAssetResults,
  });

  const boundedBody = {
    ...body,
    equity: bounded.equity,
    perAssetResults: bounded.perAssetResults,
    downsampled: bounded.downsampled,
    seriesOriginalLength: bounded.seriesOriginalLength,
  };

  return apiFetch<{ id: string; replay?: boolean }>('/backtest/results/client', {
    method: 'POST',
    headers: extraHeaders,
    body: JSON.stringify(boundedBody),
  });
}

/**
 * Get a specific backtest result by ID
 */
export async function getBacktest(id: string): Promise<ApiBacktestResult> {
  return apiFetch<ApiBacktestResult>(`/backtest/${encodeURIComponent(id)}`);
}

/**
 * Filter and sort parameters for backtest history queries
 */
export interface HistoryParams {
  limit?: number;
  offset?: number;
  strategy?: string;
  symbol?: string;
  timeframe?: string;
  exchange?: string;
  mode?: string;
  fromDate?: number;
  toDate?: number;
  minSharpe?: number;
  maxSharpe?: number;
  minReturn?: number;
  maxReturn?: number;
  sortBy?: 'runAt' | 'sharpeRatio' | 'totalReturnPercent' | 'maxDrawdownPercent' | 'winRate' | 'totalTrades';
  sortDir?: 'asc' | 'desc';
  runType?: 'strategies' | 'aggregations';
  /** Phase 3.5: filter by a specific strategy config id (backtest_runs.strategy_config_id). */
  strategyConfigId?: string;
}

/**
 * Get list of past backtest runs with pagination, filtering, and sorting
 */
export async function getHistory(params?: HistoryParams): Promise<PaginatedHistory> {
  const queryParams = new URLSearchParams();
  if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params?.offset !== undefined) queryParams.append('offset', String(params.offset));
  if (params?.strategy) queryParams.append('strategy', params.strategy);
  if (params?.symbol) queryParams.append('symbol', params.symbol);
  if (params?.timeframe) queryParams.append('timeframe', params.timeframe);
  if (params?.exchange) queryParams.append('exchange', params.exchange);
  if (params?.mode) queryParams.append('mode', params.mode);
  if (params?.fromDate !== undefined) queryParams.append('fromDate', String(params.fromDate));
  if (params?.toDate !== undefined) queryParams.append('toDate', String(params.toDate));
  if (params?.minSharpe !== undefined) queryParams.append('minSharpe', String(params.minSharpe));
  if (params?.maxSharpe !== undefined) queryParams.append('maxSharpe', String(params.maxSharpe));
  if (params?.minReturn !== undefined) queryParams.append('minReturn', String(params.minReturn));
  if (params?.maxReturn !== undefined) queryParams.append('maxReturn', String(params.maxReturn));
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortDir) queryParams.append('sortDir', params.sortDir);
  if (params?.runType) queryParams.append('runType', params.runType);
  if (params?.strategyConfigId) queryParams.append('strategyConfigId', params.strategyConfigId);
  const queryString = queryParams.toString();
  return apiFetch<PaginatedHistory>(`/backtest/history${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get backtest runs grouped by symbol
 */
export interface BacktestGroup {
  symbol: string;
  count: number;
  bestSharpe: number;
  bestReturn: number;
  timeframes: string[];
}

export async function getHistoryGroups(params?: {
  strategy?: string;
  timeframe?: string;
  mode?: string;
  minSharpe?: number;
  runType?: 'strategies' | 'aggregations';
}): Promise<{ groups: BacktestGroup[] }> {
  const queryParams = new URLSearchParams();
  if (params?.strategy) queryParams.append('strategy', params.strategy);
  if (params?.timeframe) queryParams.append('timeframe', params.timeframe);
  if (params?.mode) queryParams.append('mode', params.mode);
  if (params?.minSharpe !== undefined) queryParams.append('minSharpe', String(params.minSharpe));
  if (params?.runType) queryParams.append('runType', params.runType);
  const qs = queryParams.toString();
  return apiFetch<{ groups: BacktestGroup[] }>(`/backtest/history/groups${qs ? `?${qs}` : ''}`);
}

/**
 * Delete a backtest result
 */
export async function deleteBacktest(id: string): Promise<void> {
  return apiFetch<void>(`/backtest/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Delete all backtest history
 */
export async function deleteAllHistory(): Promise<{ message: string; count: number }> {
  return apiFetch<{ message: string; count: number }>('/backtest/history', {
    method: 'DELETE',
  });
}

// ============================================================================
// Strategy Endpoints
// ============================================================================

/**
 * Get list of all available strategies
 */
export async function getStrategies(): Promise<StrategyInfo[]> {
  return apiFetch<StrategyInfo[]>('/strategies');
}

/**
 * Get detailed information about a specific strategy
 */
export async function getStrategy(name: string): Promise<StrategyDetails> {
  return apiFetch<StrategyDetails>(`/strategies/${encodeURIComponent(name)}`);
}

// ============================================================================
// Data Endpoints
// ============================================================================

/**
 * Fetch candle data for charting
 */
export async function getCandles(params: CandleRequest): Promise<Candle[]> {
  const queryParams = new URLSearchParams({
    exchange: params.exchange,
    symbol: params.symbol,
    timeframe: params.timeframe,
    start: params.startDate,
    end: params.endDate,
  });

  const response = await apiFetch<{ candles: Candle[]; source: string; count: number }>(`/candles?${queryParams.toString()}`);
  return response.candles;
}

/**
 * Get available trading symbols for an exchange
 */
export async function getSymbols(exchange: string): Promise<string[]> {
  return apiFetch<string[]>(
    `/symbols?exchange=${encodeURIComponent(exchange)}`
  );
}

/**
 * Get list of supported exchanges
 */
export async function getExchanges(): Promise<string[]> {
  return apiFetch<string[]>('/exchanges');
}

// ============================================================================
// Optimization Endpoints
// ============================================================================

/**
 * Exact backtest configuration passed to the grid search worker.
 * Persisted alongside the results so reruns can reproduce identical metrics.
 */
export interface GridSearchBacktestConfig {
  initialCapital: number;
  leverage: number;
  takerFeeRate: number;
  slippagePercent: number;
  mode: 'spot' | 'futures';
  exchange: string;
}

/**
 * Submit a client-computed grid search result to the server for persistence.
 * Fire-and-forget — callers should .catch() to suppress errors.
 */
export async function submitClientGridSearch(body: {
  strategyName: string;
  symbol: string;
  timeframe: string;
  startDate: number;
  endDate: number;
  initialCapital: number;
  exchange?: string;
  bestParams: Record<string, unknown>;
  bestMetrics: Record<string, unknown>;
  totalCombinations: number;
  testedCombinations: number;
  topResults?: Array<{ params: Record<string, unknown>; metrics: Record<string, unknown> }>;
  backtestConfig?: GridSearchBacktestConfig;
  /** Migration 065: who owns this optimization run. */
  ownerType?: 'strategy' | 'aggregation';
  /** Migration 065: user_strategies.id or aggregation_configs.id. */
  ownerId?: string;
}): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/optimize/results/grid-search', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Submit a client-computed grid search result to the new chat-integrated endpoint.
 * This endpoint also emits an entity_created WebSocket message back to the user.
 * Fire-and-forget — callers should .catch() to suppress errors.
 *
 * @param idempotencyKey  A UUID v4 generated once per logical grid-search run
 *   (NOT per retry). Pass the same key on retries so the server returns the
 *   previously-stored result instead of inserting a duplicate.
 *   Generate with: `crypto.randomUUID()` at the time the run is queued.
 */
export async function submitChatGridSearchResult(
  body: {
    runId?: string;
    strategyName: string;
    symbol: string;
    timeframe: string;
    from: string;
    to: string;
    bestParams: Record<string, unknown>;
    bestMetrics: Record<string, unknown>;
    totalCombinations: number;
    testedCombinations: number;
    allResults: Array<{ params: Record<string, unknown>; metrics: Record<string, unknown> }>;
  },
  idempotencyKey?: string,
): Promise<{ id: string; replay?: boolean }> {
  const extraHeaders: Record<string, string> = {};
  if (idempotencyKey !== undefined) {
    extraHeaders['Idempotency-Key'] = idempotencyKey;
  }
  return apiFetch<{ id: string; replay?: boolean }>('/grid-search/results/client', {
    method: 'POST',
    headers: extraHeaders,
    body: JSON.stringify(body),
  });
}

/**
 * Submit a client-computed walk-forward result to the server for persistence.
 * Fire-and-forget — callers should .catch() to suppress errors.
 */
export async function submitClientWalkForward(body: {
  strategyName: string;
  symbol: string;
  timeframe: string;
  startDate: number;
  endDate: number;
  initialCapital: number;
  exchange?: string;
  bestParams: Record<string, unknown>;
  passed: boolean;
  averageTrainSharpe: number;
  averageTestSharpe: number;
  sharpeDegradation: number;
  windows: Array<{
    windowIndex: number;
    trainStart: number;
    trainEnd: number;
    testStart: number;
    testEnd: number;
    bestTrainParams: Record<string, unknown>;
    trainMetrics: Record<string, unknown>;
    testMetrics: Record<string, unknown>;
  }>;
  /** Migration 065: who owns this optimization run. */
  ownerType?: 'strategy' | 'aggregation';
  /** Migration 065: user_strategies.id or aggregation_configs.id. */
  ownerId?: string;
}): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/optimize/results/walk-forward', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Get all optimization runs for a strategy, symbol, and timeframe
 * Returns an array of results sorted by most recent first
 */
export async function getOptimizedParams(
  strategyName: string,
  symbol: string,
  timeframe: string
): Promise<OptimizationResult[]> {
  return apiFetch<OptimizationResult[]>(
    `/optimize/${encodeURIComponent(strategyName)}/${encodeURIComponent(symbol)}/${encodeURIComponent(timeframe)}`
  );
}

/**
 * Get the most recent optimization run for a strategy, symbol, and timeframe
 */
export async function getLatestOptimizedParams(
  strategyName: string,
  symbol: string,
  timeframe: string
): Promise<OptimizationResult> {
  return apiFetch<OptimizationResult>(
    `/optimize/${encodeURIComponent(strategyName)}/${encodeURIComponent(symbol)}/${encodeURIComponent(timeframe)}/latest`
  );
}

/**
 * Get all saved optimization results
 */
export async function getAllOptimizations(): Promise<OptimizationResult[]> {
  return apiFetch<OptimizationResult[]>('/optimize/all');
}

/**
 * Delete all optimization runs for a strategy, symbol, and timeframe
 */
export async function deleteOptimization(
  strategyName: string,
  symbol: string,
  timeframe: string
): Promise<void> {
  return apiFetch<void>(
    `/optimize/${encodeURIComponent(strategyName)}/${encodeURIComponent(symbol)}/${encodeURIComponent(timeframe)}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Delete a specific optimization run by ID
 */
export async function deleteOptimizationById(id: string): Promise<void> {
  return apiFetch<void>(
    `/optimize/id/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
    }
  );
}

// ============================================================================
// Scanner Endpoints
// ============================================================================

export interface ScanCallbacks {
  onProgress?: (progress: { current: number; total: number }) => void;
  onResult?: (result: ScanResultRow) => void;
  onDone?: (summary: ScanSummary) => void;
  onError?: (error: string) => void;
}

/**
 * Run multi-market scan with SSE streaming
 * Uses fetch + ReadableStream to consume the SSE response.
 */
export async function runScan(
  config: ScanRequest,
  callbacks?: ScanCallbacks
): Promise<void> {
  const url = `${API_BASE}/backtest/scan`;

  const response = await apiFetchRaw(url, {
    method: 'POST',
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new ApiClientError(
      `Scan request failed: ${response.status} ${response.statusText}`,
      response.status
    );
  }

  if (!response.body) {
    throw new ApiClientError('Response body is null', 500);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;

      const dataStr = line.substring(6);
      try {
        const data = JSON.parse(dataStr);

        switch (data.type) {
          case 'progress':
            callbacks?.onProgress?.({ current: data.current, total: data.total });
            break;

          case 'result':
            if (data.status === 'complete') {
              callbacks?.onResult?.({
                symbol: data.symbol,
                metrics: data.metrics,
                tradesCount: data.tradesCount,
                status: 'complete',
              });
            } else if (data.status === 'error') {
              callbacks?.onResult?.({
                symbol: data.symbol,
                metrics: { totalReturnPercent: 0, sharpeRatio: 0, maxDrawdownPercent: 0, winRate: 0, profitFactor: 0 },
                tradesCount: 0,
                status: 'error',
                error: data.error,
              });
            }
            break;

          case 'done':
            callbacks?.onDone?.(data.summary);
            break;

          case 'error':
            callbacks?.onError?.(data.error);
            break;
        }
      } catch (err) {
        console.error('Error parsing scan SSE data:', err);
      }
    }
  }
}

// ============================================================================
// Funding Rate Endpoints
// ============================================================================

/**
 * Fetch funding rate data for a given exchange/symbol/date range
 */
export async function getFundingRates(params: {
  exchange: string;
  symbol: string;
  start: number;
  end: number;
}): Promise<{ rates: Array<{ timestamp: number; fundingRate: number; markPrice?: number }> }> {
  const queryParams = new URLSearchParams({
    exchange: params.exchange,
    symbol: params.symbol,
    start: String(params.start),
    end: String(params.end),
  });
  return apiFetch<{ rates: Array<{ timestamp: number; fundingRate: number; markPrice?: number }> }>(
    `/funding-rates?${queryParams.toString()}`
  );
}

// ============================================================================
// Open Interest Endpoints
// ============================================================================

export async function getOpenInterest(params: {
  exchange: string;
  symbol: string;
  start: number;
  end: number;
}): Promise<{ records: Array<{ timestamp: number; openInterestAmount: number }> }> {
  const queryParams = new URLSearchParams({
    exchange: params.exchange,
    symbol: params.symbol,
    start: String(params.start),
    end: String(params.end),
  });
  return apiFetch<{ records: Array<{ timestamp: number; openInterestAmount: number }> }>(
    `/open-interest?${queryParams.toString()}`
  );
}

// ============================================================================
// Long/Short Ratio Endpoints
// ============================================================================

export async function getLongShortRatio(params: {
  exchange: string;
  symbol: string;
  start: number;
  end: number;
}): Promise<{ records: Array<{ timestamp: number; longRatio: number; shortRatio: number; longShortRatio: number }> }> {
  const queryParams = new URLSearchParams({
    exchange: params.exchange,
    symbol: params.symbol,
    start: String(params.start),
    end: String(params.end),
  });
  return apiFetch<{ records: Array<{ timestamp: number; longRatio: number; shortRatio: number; longShortRatio: number }> }>(
    `/long-short-ratio?${queryParams.toString()}`
  );
}

// ============================================================================
// Alt Metrics Endpoints
// ============================================================================

export async function getAltMetrics(params: {
  source: string;
  metric: string;
  start: number;
  end: number;
}): Promise<{ records: Array<{ source: string; metric: string; timestamp: number; value: number }> }> {
  const queryParams = new URLSearchParams({
    source: params.source,
    metric: params.metric,
    start: String(params.start),
    end: String(params.end),
  });
  return apiFetch<{ records: Array<{ source: string; metric: string; timestamp: number; value: number }> }>(
    `/alt-metrics?${queryParams.toString()}`
  );
}

export async function getAvailableAltMetrics(): Promise<{
  metrics: Array<{ source: string; metric: string; count: number; earliest: number; latest: number }>
}> {
  return apiFetch<{ metrics: Array<{ source: string; metric: string; count: number; earliest: number; latest: number }> }>(
    '/alt-metrics/available'
  );
}

// ============================================================================
// Aggregation Endpoints
// ============================================================================

/**
 * Get all saved aggregation configs
 */
export async function getAggregations(): Promise<AggregationConfig[]> {
  return apiFetch<AggregationConfig[]>('/aggregations');
}

// ============================================================================
// New Backtest Picker (Wave 3a)
// ============================================================================

export interface RunnablePortfolio {
  kind: 'portfolio';
  id: string;
  name: string;
  allocationMode: string;
  maxPositions: number;
  symbolCount: number;
  initialCapital: number;
  exchange: string;
}

export interface RunnableConfig {
  kind: 'config';
  id: string;
  strategyName: string;
  symbol: string;
  timeframe: string;
  params: Record<string, unknown>;
  name: string;
}

export interface RunnableTargets {
  portfolios: RunnablePortfolio[];
  configs: RunnableConfig[];
}

/**
 * Fetch all runnable targets for the "New Backtest" picker.
 *
 * Combines aggregation configs (portfolios) and strategy configs (single-symbol),
 * mirroring the planned GET /api/backtest/runnable endpoint shape.
 * Falls back gracefully if either endpoint returns an error.
 */
export async function getRunnableTargets(): Promise<RunnableTargets> {
  const [aggResult, cfgResult] = await Promise.allSettled([
    getAggregations(),
    getStrategyConfigs(),
  ]);

  const portfolios: RunnablePortfolio[] =
    aggResult.status === 'fulfilled'
      ? aggResult.value.map((a) => ({
          kind: 'portfolio' as const,
          id: a.id,
          name: a.name,
          allocationMode: a.allocationMode,
          maxPositions: a.maxPositions,
          symbolCount: a.subStrategies.length,
          initialCapital: a.initialCapital,
          exchange: a.exchange,
        }))
      : [];

  const configs: RunnableConfig[] =
    cfgResult.status === 'fulfilled'
      ? cfgResult.value.map((c) => ({
          kind: 'config' as const,
          id: c.id,
          strategyName: c.strategyName,
          symbol: c.symbol,
          timeframe: c.timeframe,
          params: c.params,
          name: c.name,
        }))
      : [];

  return { portfolios, configs };
}

/**
 * Create a new aggregation config
 */
export async function createAggregation(config: CreateAggregationRequest): Promise<AggregationConfig> {
  return apiFetch<AggregationConfig>('/aggregations', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

/**
 * Get a single aggregation config by ID
 */
export async function getAggregation(id: string): Promise<AggregationConfig> {
  return apiFetch<AggregationConfig>(`/aggregations/${encodeURIComponent(id)}`);
}

/**
 * Update an existing aggregation config
 */
export async function updateAggregation(id: string, updates: UpdateAggregationRequest): Promise<AggregationConfig> {
  return apiFetch<AggregationConfig>(`/aggregations/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete an aggregation config
 */
export async function deleteAggregation(id: string): Promise<void> {
  return apiFetch<void>(`/aggregations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Get backtest runs for a specific aggregation config
 */
export async function getAggregationRuns(aggregationId: string): Promise<BacktestSummary[]> {
  const data = await apiFetch<{ results: BacktestSummary[]; total: number }>(`/aggregations/${encodeURIComponent(aggregationId)}/runs`);
  return data.results;
}

/**
 * Run an ad-hoc aggregation backtest with an inline config (no saved aggregation ID needed)
 */
export async function runAdhocAggregation(config: {
  subStrategies: Array<{
    strategyName: string;
    symbol: string;
    timeframe: string;
    params: Record<string, unknown>;
    exchange: string;
  }>;
  allocationMode: string;
  maxPositions: number;
  initialCapital: number;
  startDate: number;
  endDate: number;
  exchange: string;
  mode?: string;
}): Promise<AggregateBacktestResult> {
  return apiFetch<AggregateBacktestResult>('/backtest/aggregate/run', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

// ============================================================================
// Config Export / Import Endpoints
// ============================================================================

/**
 * Export configs for selected run IDs. Returns a Blob for file download.
 */
export async function exportConfigs(runIds: string[]): Promise<Blob> {
  const url = `${API_BASE}/configs/export`;
  const response = await apiFetchRaw(url, {
    method: 'POST',
    body: JSON.stringify({ runIds }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Export failed' }));
    throw new ApiClientError((err as { message?: string }).message || 'Export failed', response.status);
  }
  return response.blob();
}

/**
 * Import preview response item.
 */
export interface ImportConfigPreviewItem {
  index: number;
  type: string;
  strategy: string;
  symbols: string;
  timeframe: string;
  originalMetrics?: {
    sharpeRatio: number;
    totalReturnPercent: number;
    maxDrawdownPercent: number;
  };
}

/**
 * Result item returned after running imported configs.
 */
export interface ImportConfigResultItem {
  index: number;
  strategy: string;
  symbols: string;
  status: 'success' | 'error';
  runId?: string;
  error?: string;
}

/**
 * Import configs file. When rerun=false, returns validation/preview summary.
 * When rerun=true, runs all configs and returns results.
 */
export async function importConfigs(
  file: unknown,
  rerun: boolean
): Promise<{ configs: ImportConfigPreviewItem[]; results?: ImportConfigResultItem[] }> {
  return apiFetch<{ configs: ImportConfigPreviewItem[]; results?: ImportConfigResultItem[] }>('/configs/import', {
    method: 'POST',
    body: JSON.stringify({ file, rerun }),
  });
}

// ============================================================================
// Health Check
// ============================================================================

interface HealthResponse {
  status: string;
  timestamp: string;
}

/**
 * Check if the API server is running
 */
export async function healthCheck(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('/health');
}

// ============================================================================
// Auth Endpoints
// ============================================================================

export async function login(username: string, password: string): Promise<{ token: string; user: AuthUser }> {
  // Don't use apiFetch here because login shouldn't have auth header check
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiClientError((data as { error?: string }).error || 'Login failed', response.status);
  }

  return response.json();
}

export async function getCurrentUser(): Promise<AuthUser> {
  return apiFetch<AuthUser>('/auth/me');
}

/**
 * Update onboarding progress for the authenticated user.
 * PATCH /api/auth/onboarding
 */
export async function updateOnboarding(data: UpdateOnboardingRequest): Promise<AuthUser> {
  return apiFetch<AuthUser>('/auth/onboarding', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function register(
  username: string,
  password: string,
  inviteToken: string,
): Promise<{ token: string; user: AuthUser }> {
  // Don't use apiFetch here — register is public, no auth header needed
  const response = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, inviteToken }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const errData = data as { error?: string; message?: string };
    throw new ApiClientError(errData.error ?? errData.message ?? 'Registration failed', response.status);
  }

  return response.json();
}

// ============================================================================
// Paper Trading Endpoints
// ============================================================================

export async function createPaperSession(data: CreatePaperSessionRequest): Promise<PaperSessionDetail> {
  return apiFetch<PaperSessionDetail>('/paper-trading/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listPaperSessions(): Promise<PaperSession[]> {
  return apiFetch<PaperSession[]>('/paper-trading/sessions');
}

export async function getPaperSession(id: string): Promise<PaperSessionDetail> {
  return apiFetch<PaperSessionDetail>(`/paper-trading/sessions/${id}`);
}

export async function deletePaperSession(id: string): Promise<void> {
  await apiFetch<void>(`/paper-trading/sessions/${id}`, { method: 'DELETE' });
}

export async function startPaperSession(id: string): Promise<void> {
  await apiFetch<void>(`/paper-trading/sessions/${id}/start`, { method: 'POST' });
}

export async function pausePaperSession(id: string): Promise<void> {
  await apiFetch<void>(`/paper-trading/sessions/${id}/pause`, { method: 'POST' });
}

export async function resumePaperSession(id: string): Promise<void> {
  await apiFetch<void>(`/paper-trading/sessions/${id}/resume`, { method: 'POST' });
}

export async function stopPaperSession(id: string): Promise<void> {
  await apiFetch<void>(`/paper-trading/sessions/${id}/stop`, { method: 'POST' });
}

export async function getPaperTrades(id: string, limit = 50, offset = 0): Promise<PaperTradesResponse> {
  return apiFetch<PaperTradesResponse>(`/paper-trading/sessions/${id}/trades?limit=${limit}&offset=${offset}`);
}

export async function getPaperEquity(id: string): Promise<PaperEquitySnapshot[]> {
  return apiFetch<PaperEquitySnapshot[]>(`/paper-trading/sessions/${id}/equity`);
}

export async function forcePaperTick(id: string): Promise<unknown> {
  return apiFetch<unknown>(`/paper-trading/sessions/${id}/tick`, { method: 'POST' });
}

export async function getPaperSessionEvents(id: string, limit = 100, offset = 0): Promise<PaperSessionEventsResponse> {
  return apiFetch<PaperSessionEventsResponse>(`/paper-trading/sessions/${id}/events?limit=${limit}&offset=${offset}`);
}

// ============================================================================
// Strategy Config Endpoints
// ============================================================================

export async function getStrategyConfigs(filters?: {
  strategy?: string;
  symbol?: string;
  timeframe?: string;
  userStrategyId?: string;
}): Promise<StrategyConfigListItem[]> {
  const params = new URLSearchParams();
  if (filters?.strategy) params.set('strategy', filters.strategy);
  if (filters?.symbol) params.set('symbol', filters.symbol);
  if (filters?.timeframe) params.set('timeframe', filters.timeframe);
  if (filters?.userStrategyId) params.set('userStrategyId', filters.userStrategyId);
  const queryString = params.toString();
  return apiFetch<StrategyConfigListItem[]>(`/strategy-configs${queryString ? `?${queryString}` : ''}`);
}

export async function getStrategyConfig(id: string): Promise<StrategyConfigEntity> {
  return apiFetch<StrategyConfigEntity>(`/strategy-configs/${encodeURIComponent(id)}`);
}

export async function createStrategyConfig(config: {
  strategyName: string;
  symbol: string;
  timeframe: string;
  params: Record<string, unknown>;
}): Promise<{ config: StrategyConfigEntity; created: boolean }> {
  return apiFetch<{ config: StrategyConfigEntity; created: boolean }>('/strategy-configs', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

export async function deleteStrategyConfig(id: string): Promise<{ message: string; deletedRuns: number }> {
  return apiFetch<{ message: string; deletedRuns: number }>(`/strategy-configs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function getStrategyConfigRuns(id: string): Promise<BacktestSummary[]> {
  const data = await apiFetch<{ results: BacktestSummary[]; total: number }>(`/strategy-configs/${encodeURIComponent(id)}/runs`);
  return data.results;
}

export interface BestRunSummary {
  id: string;
  sharpeRatio: number;
  totalReturnPercent: number;
  maxDrawdownPercent: number;
  totalTrades: number;
  winRate: number;
  startDate: number | null;
  endDate: number | null;
  runAt: string;
  totalRuns: number;
}

export async function getStrategyConfigBestRuns(configIds: string[]): Promise<Record<string, BestRunSummary | null>> {
  return apiFetch<Record<string, BestRunSummary | null>>('/strategy-configs/best-runs', {
    method: 'POST',
    body: JSON.stringify({ configIds }),
  });
}

export async function getStrategyConfigVersions(
  strategy: string,
  symbol: string,
  timeframe: string,
): Promise<StrategyConfigEntity[]> {
  const params = new URLSearchParams({ strategy, symbol, timeframe });
  return apiFetch<StrategyConfigEntity[]>(`/strategy-configs/versions?${params}`);
}

export interface ConfigPaperSessionSummary {
  id: string;
  name: string;
  status: 'running' | 'paused' | 'stopped' | 'error';
  initialCapital: number;
  currentEquity: number;
  createdAt: number;
  updatedAt: number;
  aggregationConfigId?: string;
}

export async function getConfigPaperSessions(configId: string): Promise<ConfigPaperSessionSummary[]> {
  return apiFetch<ConfigPaperSessionSummary[]>(`/strategy-configs/${encodeURIComponent(configId)}/paper-sessions`);
}

export async function getAggregationPaperSessions(aggregationId: string): Promise<ConfigPaperSessionSummary[]> {
  return apiFetch<ConfigPaperSessionSummary[]>(`/aggregations/${encodeURIComponent(aggregationId)}/paper-sessions`);
}

// ============================================================================
// BUG-20 — Strategy Version Migration Endpoints
// ============================================================================

/**
 * Migrate a strategy config to a target version.
 * NON-DESTRUCTIVE: creates a NEW pinned config at the target version.
 * Returns the new config ID and any dropped/added param keys.
 */
export async function migrateStrategyConfig(
  configId: string,
  toVersion: number,
): Promise<MigrateStrategyConfigResponse> {
  return apiFetch<MigrateStrategyConfigResponse>(
    `/strategy-configs/${encodeURIComponent(configId)}/migrate`,
    {
      method: 'POST',
      body: JSON.stringify({ toVersion }),
    },
  );
}

/**
 * Migrate an aggregation config to a target version (uniform or per-sub).
 * NON-DESTRUCTIVE: creates a NEW pinned aggregation at the target version(s).
 * Returns the new aggregation ID, sub versions, and per-sub dropped/added keys.
 */
export async function migrateAggregation(
  aggregationId: string,
  toVersion: number | Record<string, number>,
): Promise<MigrateAggregationResponse> {
  return apiFetch<MigrateAggregationResponse>(
    `/aggregations/${encodeURIComponent(aggregationId)}/migrate`,
    {
      method: 'POST',
      body: JSON.stringify({ toVersion }),
    },
  );
}

/**
 * Subscribe to SSE stream for a paper trading session.
 * Returns an unsubscribe function.
 */
export function subscribePaperSession(
  sessionId: string,
  onEvent: (event: PaperTradingEvent) => void,
  onError?: (error: Error) => void,
): () => void {
  const url = `${API_BASE}/paper-trading/sessions/${sessionId}/stream`;
  let abortController: AbortController | null = new AbortController();

  (async () => {
    try {
      const response = await apiFetchRaw(url, { signal: abortController?.signal });
      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.substring(6)) as PaperTradingEvent;
            onEvent(event);
          } catch {
            // Skip malformed events
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  })();

  return () => {
    abortController?.abort();
    abortController = null;
  };
}

// ============================================================================
// Platform Settings — Kill Switch
// ============================================================================

export async function getKillSwitchSettings(): Promise<KillSwitchSettings> {
  return apiFetch<KillSwitchSettings>('/settings/kill-switch');
}

export async function updatePtKillSwitch(data: UpdateKillSwitchRequest): Promise<KillSwitchConfig> {
  return apiFetch<KillSwitchConfig>('/settings/kill-switch/pt', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateLtKillSwitch(data: UpdateKillSwitchRequest): Promise<KillSwitchConfig> {
  return apiFetch<KillSwitchConfig>('/settings/kill-switch/lt', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Credentials Endpoints
// ============================================================================

export async function listCredentials(): Promise<CredentialSummary[]> {
  return apiFetch<CredentialSummary[]>('/credentials');
}

export async function createCredential(data: CreateCredentialRequest): Promise<CredentialSummary> {
  return apiFetch<CredentialSummary>('/credentials', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteCredential(id: string): Promise<{ success: true }> {
  return apiFetch<{ success: true }>(`/credentials/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function testCredential(id: string): Promise<TestCredentialResult> {
  return apiFetch<TestCredentialResult>(`/credentials/${encodeURIComponent(id)}/test`, {
    method: 'POST',
  });
}

// ============================================================================
// User Strategies Endpoints
// ============================================================================

/**
 * Get all user strategies for the authenticated user
 */
export async function getUserStrategies(): Promise<UserStrategy[]> {
  return apiFetch<UserStrategy[]>('/user-strategies');
}

/**
 * Get a single user strategy by ID
 */
export async function getUserStrategy(id: string): Promise<UserStrategy> {
  return apiFetch<UserStrategy>(`/user-strategies/${encodeURIComponent(id)}`);
}

// ============================================================================
// Strategies page sub-endpoints (Phase 3.5)
// ============================================================================

/** Compact version summary for the strategy version picker / timeline. */
export interface StrategyVersionSummary {
  id: string;
  strategyId: string;
  /** Integer snapshot counter (1..N) — used as the switch/load key. */
  version: number;
  /**
   * Semver string declared in the snapshot's code header (e.g. "8.3.0").
   * null when no recognisable version declaration was found in the code.
   */
  semver?: string | null;
  name: string;
  /** TypeScript source code for this version snapshot. */
  code: string;
  /**
   * Params schema stored for this version snapshot.
   * null when the snapshot predates params_schema storage.
   */
  paramsSchema: StrategyParam[] | null;
  changeSummary: string | null;
  createdAt: number;
}

/**
 * List all version snapshots for a user strategy (no code blobs).
 * Returns versions ordered oldest → newest.
 */
export async function listUserStrategyVersions(id: string): Promise<StrategyVersionSummary[]> {
  return apiFetch<StrategyVersionSummary[]>(`/user-strategies/${encodeURIComponent(id)}/versions`);
}

/** One config enriched with its GS-run linkage (returned by /api/strategies/:id/configs). */
export interface ConfigWithGsLink {
  id: string;
  strategyName: string;
  symbol: string;
  timeframe: string;
  params: Record<string, unknown>;
  contentHash: string;
  name: string;
  userId?: string;
  userStrategyId?: string;
  strategyVersion?: number;
  createdAt: number;
  gsRunId: string | null;
  gsRanAt: number | null;
}

/**
 * A parameter derived from the UNION of param keys across a strategy's configs.
 * Returned by GET /api/strategies/:id/configs when user_strategies.params is empty.
 * Phase 3.5, Task B.
 */
export interface DerivedParam {
  /** Parameter name (key in configs.params JSONB). */
  name: string;
  /** Inferred TypeScript type from a sample value across configs. */
  type: 'number' | 'boolean' | 'string' | 'unknown';
  /**
   * A representative value from one of the strategy's configs.
   * null when all configs have null for this key.
   */
  sampleValue: unknown;
}

/** Grouped configs shape returned by GET /api/strategies/:id/configs. */
export interface StrategyGroupedConfigs {
  manual: ConfigWithGsLink[];
  gridSearches: Array<{ id: string; ranAt: number; configs: ConfigWithGsLink[] }>;
  /**
   * Task B (Phase 3.5): UNION of param keys across all configs for this strategy.
   * Non-empty when user_strategies.params = [] (no declared schema).
   * Use as fallback for the Parameters section when strategy.params is empty.
   */
  derivedParams: DerivedParam[];
}

/**
 * Fetch grouped configs for a user strategy.
 * Manual configs first, then GS groups.
 */
export async function getStrategyGroupedConfigs(strategyId: string): Promise<StrategyGroupedConfigs> {
  return apiFetch<StrategyGroupedConfigs>(`/strategies/${encodeURIComponent(strategyId)}/configs`);
}

/** One portfolio that references a config from this strategy. */
export interface StrategyUsedInPortfolioItem {
  aggregationId: string;
  name: string;
  allocationMode: string;
  currentVersion: number;
  symbolsUsingThisStrategy: string[];
}

/**
 * Return all non-aof1 portfolios referencing at least one config from this strategy.
 *
 * @param version - When provided, only portfolios whose matching configs are pinned
 *   to this exact strategy_version integer are returned.
 */
export async function getStrategyUsedInPortfolios(
  strategyId: string,
  version?: number,
): Promise<StrategyUsedInPortfolioItem[]> {
  const qs = version != null ? `?version=${encodeURIComponent(String(version))}` : '';
  return apiFetch<StrategyUsedInPortfolioItem[]>(`/strategies/${encodeURIComponent(strategyId)}/used-in-portfolios${qs}`);
}

/**
 * Compact optimization summary for the strategy detail "Optimizations" section.
 * Returned by GET /api/strategies/:id/optimizations (migration 065).
 */
export interface StrategyOptimizationSummary {
  id: string;
  type: 'grid_search' | 'walk_forward';
  symbol: string;
  timeframe: string;
  sharpeRatio: number;
  totalReturnPercent: number;
  totalTrades: number;
  optimizedAt: number;
  startDate?: number;
  endDate?: number;
}

/**
 * Fetch all GS and WF runs owned by this strategy (migration 065 owner link).
 * Returns most-recent first.
 */
export async function getStrategyOptimizations(strategyId: string): Promise<StrategyOptimizationSummary[]> {
  return apiFetch<StrategyOptimizationSummary[]>(`/strategies/${encodeURIComponent(strategyId)}/optimizations`);
}

/**
 * Fetch all GS and WF runs owned by this portfolio/aggregation (migration 065 owner link).
 * Returns most-recent first.
 * Shape is identical to StrategyOptimizationSummary — same compact row per optimization run.
 */
export async function getAggregationOptimizations(aggregationId: string): Promise<StrategyOptimizationSummary[]> {
  return apiFetch<StrategyOptimizationSummary[]>(`/aggregations/${encodeURIComponent(aggregationId)}/optimizations`);
}

/** Compact portfolio version row returned by GET /api/aggregations/:id/versions. */
export interface PortfolioVersionSummary {
  id: string;
  portfolioId: string;
  version: number;
  name: string;
  allocationMode: string;
  maxPositions: number;
  initialCapital: number;
  exchange: string;
  changeSummary: string | null;
  createdAt: number;
}

/**
 * Fetch version history for a portfolio (aggregation config).
 */
export async function getAggregationVersions(aggregationId: string): Promise<PortfolioVersionSummary[]> {
  return apiFetch<PortfolioVersionSummary[]>(`/aggregations/${encodeURIComponent(aggregationId)}/versions`);
}

/**
 * Shape returned by GET /api/user-strategies/by-name/:name/versions/:version
 * (added by WP2 of BUG-20 fix)
 */
export interface UserStrategyVersion {
  strategyId: string;
  name: string;
  description: string;
  version: number;
  code: string;
  compiledCode: string;
  paramsSchema: StrategyParam[];
  createdAt: number;
}

/**
 * Fetch a PINNED strategy version by name and integer version number.
 * Returns null when the version is not found (404).
 * Used by the client-side backtest worker to resolve version-pinned code
 * for aggregation configs that have subStrategyVersions set (BUG-20 fix).
 */
export async function getUserStrategyVersion(
  name: string,
  version: number,
): Promise<UserStrategyVersion | null> {
  try {
    return await apiFetch<UserStrategyVersion>(
      `/user-strategies/by-name/${encodeURIComponent(name)}/versions/${version}`,
    );
  } catch (err) {
    // Treat 404 as "not found" → return null so callers can decide behavior
    if (err instanceof Error && err.message.includes('404')) {
      return null;
    }
    throw err;
  }
}

/**
 * Create a new user strategy
 */
export async function createUserStrategy(data: {
  name: string;
  description?: string;
  code: string;
}): Promise<UserStrategy> {
  return apiFetch<UserStrategy>('/user-strategies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing user strategy
 */
export async function updateUserStrategy(
  id: string,
  data: { name?: string; description?: string; code?: string },
): Promise<UserStrategy> {
  return apiFetch<UserStrategy>(`/user-strategies/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a user strategy
 */
export async function deleteUserStrategy(id: string): Promise<void> {
  return apiFetch<void>(`/user-strategies/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// API Token Endpoints
// ============================================================================

/**
 * List all API tokens for the authenticated user (token values are never returned)
 */
export async function listApiTokens(): Promise<ApiTokenSummary[]> {
  return apiFetch<ApiTokenSummary[]>('/auth/api-tokens');
}

/**
 * Create a new API token. The full token is returned ONCE in the response.
 */
export async function createApiToken(data: CreateApiTokenRequest): Promise<ApiTokenCreated> {
  return apiFetch<ApiTokenCreated>('/auth/api-tokens', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Revoke (delete) an API token by ID
 */
export async function deleteApiToken(id: string): Promise<void> {
  return apiFetch<void>(`/auth/api-tokens/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Optimizations Endpoints (Grid Search + Walk-Forward history)
// ============================================================================

import type {
  OptimizationListResponse,
  OptimizationDetail as OptimizationDetailType,
  OptimizationsQueryParams,
} from '../types/optimizations.js';

// Re-export types so callers can import them from the client module if desired
export type { OptimizationListResponse, OptimizationDetailType, OptimizationsQueryParams };

/**
 * Fetch a paginated list of past grid search or walk-forward runs.
 * Throws ApiClientError on non-2xx responses (Rule 11: no silent fallbacks).
 */
export async function fetchOptimizations(
  params: OptimizationsQueryParams
): Promise<OptimizationListResponse> {
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.strategyName) qs.set('strategyName', params.strategyName);
  if (params.symbol) qs.set('symbol', params.symbol);
  if (params.timeframe) qs.set('timeframe', params.timeframe);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString();
  return apiFetch<OptimizationListResponse>(`/optimizations${query ? `?${query}` : ''}`);
}

/**
 * Fetch full detail for a single optimization run (grid search or walk-forward).
 * Throws ApiClientError on non-2xx responses (including 404 not found).
 */
export async function fetchOptimizationDetail(id: string): Promise<OptimizationDetailType> {
  return apiFetch<OptimizationDetailType>(`/optimizations/${encodeURIComponent(id)}`);
}

// ============================================================================
// Admin Endpoints
// ============================================================================

export interface DataCacheSummary {
  totalCandles: number;
  totalFundingRates: number;
  totalOpenInterest: number;
  totalLongShortRatio: number;
  totalAltMetrics: number;
  totalLiquidations: number;
  dbSizeBytes: number;
}

export interface DataCacheCandleRow {
  exchange: string;
  symbol: string;
  timeframe: string;
  count: number;
  earliest: string;
  latest: string;
}

export interface DataCacheFundingRow {
  exchange: string;
  symbol: string;
  count: number;
  earliest: string;
  latest: string;
}

export interface DataCacheOIRow {
  exchange: string;
  symbol: string;
  count: number;
  earliest: string;
  latest: string;
}

export interface DataCacheLSRRow {
  exchange: string;
  symbol: string;
  count: number;
  earliest: string;
  latest: string;
}

export interface DataCacheAltMetricRow {
  source: string;
  metric: string;
  count: number;
  earliest: string;
  latest: string;
  latestValue: number;
}

export interface DataCacheLiquidationRow {
  source: string;
  symbol: string;
  count: number;
  earliest: string;
  latest: string;
}

export interface DataCacheStats {
  summary: DataCacheSummary;
  candles: DataCacheCandleRow[];
  fundingRates: DataCacheFundingRow[];
  openInterest: DataCacheOIRow[];
  longShortRatio: DataCacheLSRRow[];
  altMetrics: DataCacheAltMetricRow[];
  liquidations: DataCacheLiquidationRow[];
}

/**
 * Fetch data cache statistics (admin only).
 */
export async function getDataCacheStats(): Promise<DataCacheStats> {
  return apiFetch<DataCacheStats>('/admin/data-cache');
}

// ============================================================================
// Cache Refresh Endpoints
// ============================================================================

export interface CacheJobStatus {
  id: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt?: string;
  types: string[];
  currentStep?: string;
  output: string[];
  errors: string[];
}

/**
 * Trigger a cache refresh job (admin only).
 * Optionally filter to specific types (e.g. ['candles', 'funding']).
 */
export async function triggerCacheRefresh(
  types?: string[]
): Promise<{ status: string; jobId: string; types: string[] }> {
  return apiFetch<{ status: string; jobId: string; types: string[] }>(
    '/admin/cache/refresh',
    {
      method: 'POST',
      body: JSON.stringify(types ? { types } : {}),
    }
  );
}

/**
 * Poll the status of a cache refresh job (admin only).
 */
export async function getCacheRefreshStatus(jobId: string): Promise<CacheJobStatus> {
  return apiFetch<CacheJobStatus>(`/admin/cache/refresh/${jobId}`);
}

// ============================================================================
// Data Health Endpoints
// ============================================================================

export interface GapInfo {
  gapAfterTimestamp: number;
  gapBeforeTimestamp: number;
  missingCount: number;
}

export interface ConsistencyReport {
  type: string;
  exchange?: string;
  symbol?: string;
  timeframe?: string;
  source?: string;
  metric?: string;
  status: 'ok' | 'warning' | 'error';
  totalExpected: number;
  totalActual: number;
  gaps: GapInfo[];
  missingCount: number;
  checkedRange: { from: number; to: number };
  checkedAt: string;
}

export interface CategoryHealth {
  status: 'ok' | 'warning' | 'error';
  totalDatasets: number;
  healthyDatasets: number;
  datasetsWithGaps: number;
  totalMissingItems: number;
  details: ConsistencyReport[];
}

export interface DataHealthReport {
  overall: 'ok' | 'warning' | 'error';
  checkedAt: string;
  categories: {
    candles: CategoryHealth;
    funding: CategoryHealth;
    oi: CategoryHealth;
    lsr: CategoryHealth;
    liquidations: CategoryHealth;
    altMetrics: CategoryHealth;
  };
}

export interface CronSchedule {
  incrementalRefreshIntervalMin: number;
  consistencyCheckIntervalMin: number;
  dailyFullRefreshUtcHour: number;
  nextDailyRefresh: string;
}

export async function getDataHealth(cached?: boolean): Promise<DataHealthReport> {
  const params = cached ? '?cached=true' : '';
  return apiFetch<DataHealthReport>(`/admin/data-health${params}`);
}

export async function triggerDataRepair(params: {
  type: string;
  exchange?: string;
  symbol?: string;
  timeframe?: string;
  source?: string;
  metric?: string;
}): Promise<{ status: string; jobId: string }> {
  return apiFetch<{ status: string; jobId: string }>('/admin/data-repair', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function triggerRepairAll(): Promise<{ status: string; jobId: string }> {
  return apiFetch<{ status: string; jobId: string }>('/admin/data-repair/all', {
    method: 'POST',
  });
}

export async function getRepairStatus(): Promise<{ running: boolean; job: CacheJobStatus | null }> {
  return apiFetch<{ running: boolean; job: CacheJobStatus | null }>('/admin/data-repair/status');
}

export async function getCronSchedule(): Promise<CronSchedule> {
  return apiFetch<CronSchedule>('/admin/cron-schedule');
}

// ============================================================================
// Admin Activity / Observability Endpoints
// ============================================================================

export interface UserActivityRow {
  userId: string;
  username: string;
  role: string;
  userCreatedAt: string;
  backtestCount: number;
  optimizationCount: number;
  paperSessionCount: number;
  jobCount: number;
  jobFailedCount: number;
  lastBacktestAt: string | null;
  lastOptimizationAt: string | null;
  lastJobAt: string | null;
  lastActiveAt: string | null;
}

export interface ActivityTrendEntry {
  date: string;
  backtests: number;
  gridSearch: number;
  walkForward: number;
}

export interface ActivityTrendsResult {
  days: number;
  series: ActivityTrendEntry[];
}

export interface ComputeKindStat {
  kind: string;
  total: number;
  completed: number;
  failed: number;
  otherTerminal: number;
  avgDurationS: number | null;
  p50DurationS: number | null;
  p95DurationS: number | null;
  avgQueueWaitS: number | null;
}

export interface ComputeDailyEntry {
  date: string;
  completed: number;
  failed: number;
}

export interface ComputeOverall {
  successRate: number | null;
  avgDurationS: number | null;
  p95DurationS: number | null;
}

export interface ComputeStatsResult {
  byKind: ComputeKindStat[];
  daily: ComputeDailyEntry[];
  overall: ComputeOverall;
}

export interface SecurityTopIp {
  ip: string;
  attempts: number;
  lastAttempt: string;
  reasons: string[];
}

export interface SecurityHourlyEntry {
  hour: string;
  count: number;
}

export interface McpErrorByCode {
  errorCode: string;
  count: number;
}

export interface SecurityStatsResult {
  topIps: SecurityTopIp[];
  hourlyHistogram: SecurityHourlyEntry[];
  mcpErrorsByCode: McpErrorByCode[];
  totalFailedAttempts: number;
  totalMcpErrors: number;
}

export interface AdminLiveProcessStats {
  uptimeS: number;
  memoryRss: number;
  memoryHeapUsed: number;
  memoryHeapTotal: number;
  cpuPercent: number;
  eventLoopLagMeanMs: number;
  eventLoopLagP99Ms: number;
}

export interface AdminLiveDbPool {
  totalCount: number;
  idleCount: number;
  waitingCount: number;
  max: number;
}

export interface AdminLiveQueue {
  queued: number;
  running: number;
  activeWorkers: number;
}

export interface AdminLiveApiLoad {
  activeTokens24h: number;
  totalTokens: number;
}

export interface AdminLiveRequestCounters {
  totalRequests: number;
  total4xx: number;
  total5xx: number;
  last60s: { requests: number; errors: number };
  sinceStartedAt: string;
}

export interface AdminLiveCron {
  incrementalRefreshIntervalMin: number;
  consistencyCheckIntervalMin: number;
  dailyFullRefreshUtcHour: number;
  nextDailyRefresh: string;
}

export interface AdminLiveSnapshot {
  capturedAt: string;
  process: AdminLiveProcessStats;
  dbPool: AdminLiveDbPool;
  queue: AdminLiveQueue;
  apiLoad: AdminLiveApiLoad;
  requestCounters: AdminLiveRequestCounters;
  cron: AdminLiveCron;
}

/**
 * Fetch per-user activity leaderboard (admin only).
 */
export async function getAdminUserActivity(): Promise<{ users: UserActivityRow[] }> {
  return apiFetch<{ users: UserActivityRow[] }>('/admin/metrics/users');
}

/**
 * Fetch activity trends over the given number of days (admin only).
 */
export async function getAdminTrends(days = 30): Promise<ActivityTrendsResult> {
  return apiFetch<ActivityTrendsResult>(`/admin/metrics/trends?days=${days}`);
}

/**
 * Fetch compute job statistics over the given number of days (admin only).
 */
export async function getAdminCompute(days = 30): Promise<ComputeStatsResult> {
  return apiFetch<ComputeStatsResult>(`/admin/metrics/compute?days=${days}`);
}

/**
 * Fetch a live process / infrastructure snapshot (admin only).
 */
export async function getAdminLive(): Promise<AdminLiveSnapshot> {
  return apiFetch<AdminLiveSnapshot>('/admin/metrics/live');
}

/**
 * Fetch security / failed-auth statistics over the given window (admin only).
 * windowMinutes: 1–43200 (default 1440 = 24h).
 */
export async function getAdminSecurity(windowMinutes = 1440): Promise<SecurityStatsResult> {
  return apiFetch<SecurityStatsResult>(`/admin/metrics/security?windowMinutes=${windowMinutes}`);
}

// ============================================================================
// Notification Settings
// ============================================================================

export interface NotificationSettings {
  telegramChatId: string | null;
}

export async function getNotificationSettings(): Promise<NotificationSettings> {
  return apiFetch<NotificationSettings>('/me/notification-settings');
}

export async function updateNotificationSettings(
  settings: NotificationSettings,
): Promise<NotificationSettings> {
  return apiFetch<NotificationSettings>('/me/notification-settings', {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

// ============================================================================
// Telegram Link Token
// ============================================================================

export interface TelegramLinkToken {
  url: string;
  token: string;
  expiresAt: number;
}

/**
 * Request a one-time deep-link token for connecting a Telegram account.
 * The returned `url` is `https://t.me/<bot>?start=<token>`.
 *
 * May throw ApiClientError with statusCode 503 if the bot is not configured.
 */
export async function createTelegramLinkToken(): Promise<TelegramLinkToken> {
  return apiFetch<TelegramLinkToken>('/me/telegram/link-token', {
    method: 'POST',
  });
}
